/**
 * Configuration utility for managing environment variables
 * Provides type-safe access to all application configuration
 */

interface ApiConfig {
  baseUrl: string;
  timeout: number;
}

interface SearchConfig {
  vectorThreshold: number;
  eventLimit: number;
  locationLimit: number;
  entityLimit: number;
  defaultLimit: number;
  typeaheadLimit: number;
}

interface PaginationConfig {
  eventsDefaultLimit: number;
  partnersDefaultLimit: number;
  locationsDefaultLimit: number;
  popularEventsLimit: number;
  topCitiesLimit: number;
  /** Entity pages have no pagination, so they fetch the whole expected dataset. */
  entityEventsLimit: number;
}

interface EventDisplayConfig {
  ongoingDurationHours: number;
}

interface ExternalServicesConfig {
  nominatimBaseUrl: string;
  nominatimRateLimitDelay: number;
  leafletTileUrl: string;
  leafletTileAttribution: string;
}

interface LeafletIconsConfig {
  iconUrl: string;
  iconRetinaUrl: string;
  shadowUrl: string;
}

interface AppConfig {
  name: string;
  version: string;
  description: string;
  debugMode: boolean;
  logLevel: string;
  feedbackFormUrl: string;
}

interface FeatureConfig {
  entityLogos: boolean;
  enhancedEventDetail: boolean;
  relatedEvents: boolean;
}

interface Config {
  api: ApiConfig;
  search: SearchConfig;
  pagination: PaginationConfig;
  eventDisplay: EventDisplayConfig;
  externalServices: ExternalServicesConfig;
  leafletIcons: LeafletIconsConfig;
  app: AppConfig;
  features: FeatureConfig;
}

/**
 * Get environment variable with fallback value
 */
const getEnvVar = (key: string, fallback: string): string => {
  const value = process.env[key];
  if (value === undefined || value === '') {
    console.warn(`Environment variable ${key} is not set, using fallback: ${fallback}`);
    return fallback;
  }
  return value;
};

/**
 * Get environment variable as number with fallback
 */
const getEnvNumber = (key: string, fallback: number): number => {
  const value = process.env[key];
  if (value === undefined || value === '') {
    console.warn(`Environment variable ${key} is not set, using fallback: ${fallback}`);
    return fallback;
  }
  const numValue = parseInt(value, 10);
  if (isNaN(numValue)) {
    console.warn(`Environment variable ${key} is not a valid number, using fallback: ${fallback}`);
    return fallback;
  }
  return numValue;
};

/**
 * Get environment variable as boolean with fallback
 */
const getEnvBoolean = (key: string, fallback: boolean): boolean => {
  const value = process.env[key];
  if (value === undefined || value === '') {
    console.warn(`Environment variable ${key} is not set, using fallback: ${fallback}`);
    return fallback;
  }
  return value.toLowerCase() === 'true';
};

/**
 * Get environment variable as float with fallback
 */
const getEnvFloat = (key: string, fallback: number): number => {
  const value = process.env[key];
  if (value === undefined || value === '') {
    console.warn(`Environment variable ${key} is not set, using fallback: ${fallback}`);
    return fallback;
  }
  const floatValue = parseFloat(value);
  if (isNaN(floatValue)) {
    console.warn(`Environment variable ${key} is not a valid number, using fallback: ${fallback}`);
    return fallback;
  }
  return floatValue;
};

/**
 * Determine API base URL based on environment
 */
const getApiBaseUrl = (): string => {
  if (process.env.NODE_ENV === 'production') {
    return '/api';
  }
  return getEnvVar('REACT_APP_API_BASE_URL', 'http://localhost:7071/api');
};

const getLeafletTileUrl = (): string => {
  const tileUrl = getEnvVar(
    'REACT_APP_LEAFLET_TILE_URL',
    'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
  );
  const cartoBasemapKey = process.env.REACT_APP_CARTO_BASEMAP_KEY?.trim();

  if (!cartoBasemapKey || !tileUrl.includes('basemaps.cartocdn.com') || /[?&]key=/.test(tileUrl)) {
    return tileUrl;
  }

  const separator = tileUrl.includes('?') ? '&' : '?';
  return `${tileUrl}${separator}key=${encodeURIComponent(cartoBasemapKey)}`;
};

/**
 * Application configuration object
 */
export const config: Config = {
  api: {
    baseUrl: getApiBaseUrl(),
    timeout: getEnvNumber('REACT_APP_API_TIMEOUT', 30000),
  },
  search: {
    vectorThreshold: getEnvFloat('REACT_APP_SEARCH_VECTOR_THRESHOLD', 0.3),
    eventLimit: getEnvNumber('REACT_APP_SEARCH_EVENT_LIMIT', 6),
    locationLimit: getEnvNumber('REACT_APP_SEARCH_LOCATION_LIMIT', 4),
    entityLimit: getEnvNumber('REACT_APP_SEARCH_ENTITY_LIMIT', 4),
    defaultLimit: getEnvNumber('REACT_APP_SEARCH_DEFAULT_LIMIT', 20),
    typeaheadLimit: getEnvNumber('REACT_APP_SEARCH_TYPEAHEAD_LIMIT', 10),
  },
  pagination: {
    eventsDefaultLimit: getEnvNumber('REACT_APP_EVENTS_DEFAULT_LIMIT', 20),
    partnersDefaultLimit: getEnvNumber('REACT_APP_PARTNERS_DEFAULT_LIMIT', 50),
    locationsDefaultLimit: getEnvNumber('REACT_APP_LOCATIONS_DEFAULT_LIMIT', 50),
    popularEventsLimit: getEnvNumber('REACT_APP_POPULAR_EVENTS_LIMIT', 18),
    topCitiesLimit: getEnvNumber('REACT_APP_TOP_CITIES_LIMIT', 18),
    entityEventsLimit: getEnvNumber('REACT_APP_ENTITY_EVENTS_LIMIT', 250),
  },
  eventDisplay: {
    ongoingDurationHours: getEnvNumber('REACT_APP_ONGOING_EVENT_DURATION_HOURS', 72),
  },
  externalServices: {
    nominatimBaseUrl: getEnvVar('REACT_APP_NOMINATIM_BASE_URL', 'https://nominatim.openstreetmap.org/search'),
    nominatimRateLimitDelay: getEnvNumber('REACT_APP_NOMINATIM_RATE_LIMIT_DELAY', 100),
    // CARTO Voyager requires a domain-restricted browser key in production.
    // OpenStreetMap's tile servers (`tile.openstreetmap.org`) are NOT for app
    // use and will return 403 "Access blocked" placeholder tiles.
    leafletTileUrl: getLeafletTileUrl(),
    leafletTileAttribution: getEnvVar(
      'REACT_APP_LEAFLET_TILE_ATTRIBUTION',
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    ),
  },
  leafletIcons: {
    iconUrl: getEnvVar('REACT_APP_LEAFLET_ICON_URL', 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png'),
    iconRetinaUrl: getEnvVar('REACT_APP_LEAFLET_ICON_RETINA_URL', 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png'),
    shadowUrl: getEnvVar('REACT_APP_LEAFLET_SHADOW_URL', 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'),
  },
  app: {
    name: getEnvVar('REACT_APP_NAME', 'Event Finder'),
    version: getEnvVar('REACT_APP_VERSION', '1.0.0'),
    description: getEnvVar('REACT_APP_DESCRIPTION', 'Discover and explore tech company events with AI-powered search'),
    debugMode: getEnvBoolean('REACT_APP_DEBUG_MODE', false),
    logLevel: getEnvVar('REACT_APP_LOG_LEVEL', 'error'),
    feedbackFormUrl: getEnvVar('REACT_APP_FEEDBACK_FORM', 'https://forms.cloud.microsoft/r/ev9WHkqq39'),
  },
  features: {
    entityLogos: getEnvBoolean('REACT_APP_ENTITY_LOGOS_ENABLED', false),
    enhancedEventDetail: getEnvBoolean('REACT_APP_ENHANCED_EVENT_DETAIL_ENABLED', false),
    relatedEvents: getEnvBoolean('REACT_APP_RELATED_EVENTS_ENABLED', false),
  },
};

export default config;
